import ResultFeedback from "../components/resultFeedback.json";

/*
 * D-19 Report Cluster Mapping
 *
 * Source:
 * EntreMap Phase 2 Direction, Section 10 - Results display grouping.
 *
 * Important:
 * - Existing survey scoring is not changed.
 * - Only measures currently available in resultSet are included.
 * - Measures requested by the client but not currently calculated are
 *   documented separately below and are not assigned fabricated scores.
 *
 * Cluster scores are descriptive display scores only. Each available
 * measure is first normalised to 0-100 using its configured scale maximum.
 * The cluster display score is the arithmetic mean of those available
 * normalised measure scores.
 */

export const REPORT_CLUSTERS = [
  {
    id: "motivation-drive",
    name: "Motivation and Drive",
    meaning: "Why you move toward entrepreneurial action.",
    measures: [
      {
        name: "Motivation",
        path: "MotivationAtWork.Total",
        feedbackPath: "MotivationAtWork.Total",
      },
      {
        name: "Entrepreneurial Passion",
        path: "EntrepreneurialPassion.Total",
        feedbackPath: "EntrepreneurialPassion.Total",
      },
      {
        name: "Need for Achievement",
        path: "NeedToAchieve.Total",
        feedbackPath: "NeedToAchieve.Total",
      },
    ],
    unavailableMeasures: [
      "Entrepreneurial Identity",
      "Entrepreneurial Intention",
    ],
  },

  {
    id: "opportunity-ideas",
    name: "Opportunity and Ideas",
    meaning: "How you notice, explore and create possibilities.",
    measures: [
      {
        name: "Curiosity",
        path: "Curiosity.Total",
        feedbackPath: "Curiosity.Total",
      },
    ],
    unavailableMeasures: [
      "Entrepreneurial Alertness",
      "Entrepreneurial Imaginativeness",
      "Opportunity Recognition",
    ],
  },

  {
    id: "action-execution",
    name: "Action and Execution",
    meaning: "How you turn ideas into progress.",
    measures: [
      {
        name: "Proactivity",
        path: "IndividualEntrepreneurialOrientation.Proactivity",
        feedbackPath: "IndividualEntrepreneurialOrientation.Proactivity",
      },
      {
        name: "Entrepreneurial Self-Efficacy",
        path: "EntrepreneurialSelfEfficacy.Total",
        feedbackPath: "EntrepreneurialSelfEfficacy.Total",
      },
      {
        name: "Grit",
        path: "Grit.Total",
        feedbackPath: "Grit.Total",
      },
    ],
    unavailableMeasures: [
      "Opportunity Exploitation",
      "Bricolage",
    ],
  },

  {
    id: "decision-uncertainty",
    name: "Decision-Making Under Uncertainty",
    meaning: "How you respond when outcomes are unclear.",
    measures: [
      {
        name: "Risk-Taking",
        path: "IndividualEntrepreneurialOrientation.RiskTaking",
        feedbackPath: "IndividualEntrepreneurialOrientation.RiskTaking",
      },
      {
        name: "Tolerance for Ambiguity",
        path: "ToleranceForAmbiguity.Total",
        feedbackPath: "ToleranceForAmbiguity.Total",
      },
      {
        name: "Fear of Failure",
        path: "FearOfFailure.Total",
        feedbackPath: "FearOfFailure.Total",
      },
    ],
    unavailableMeasures: [
      "Effectuation/Causation",
    ],
  },

  {
    id: "learning-adaptation",
    name: "Learning and Adaptation",
    meaning: "How you recover and keep developing.",
    measures: [
      {
        name: "Growth Mindset",
        path: "GrowthMindset.Total",
        feedbackPath: "GrowthMindset.Total",
      },
      {
        name: "Resilience",
        path: "Resilience.Total",
        feedbackPath: "Resilience.Total",
      },
      {
        name: "Cognitive Flexibility",
        path: "CognitiveFlexibility.Total",
        feedbackPath: "CognitiveFlexibility.Total",
      },
    ],
    unavailableMeasures: [
      "Metacognition",
    ],
  },

  {
    id: "people-stakeholders",
    name: "People and Stakeholders",
    meaning: "How you understand and work with others.",
    measures: [
      {
        name: "Empathy",
        path: "Empathy.Total",
        feedbackPath: "Empathy.Total",
      },
      {
        name: "Social Curiosity",
        path: "Curiosity.OvertSocial",
        feedbackPath: "Curiosity.OvertSocial",
      },
    ],
    unavailableMeasures: [
      "Relatedness",
    ],
  },

  {
    id: "personal-patterns",
    name: "Personal Patterns",
    meaning: "Deeper tendencies that may shape behaviour.",
    measures: [
      {
        name: "Big Five",
        path: "TheBigFive.Total",
        feedbackPath: "TheBigFive.Total",
      },
      {
        name: "Impostor",
        path: "ImposterSyndrome.Total",
        feedbackPath: "ImposterSyndrome.Total",
      },
      {
        name: "Rule-Breaking / Nonconformity",
        path: "RuleBreaking.Total",
        feedbackPath: "RuleBreaking.Total",
      },
      {
        name: "Harmonious Passion",
        path: "PassionScale.HarmoniousPassion",
        feedbackPath: "PassionScale.HarmoniousPassion",
      },
      {
        name: "Obsessive Passion",
        path: "PassionScale.ObsessivePassion",
        feedbackPath: "PassionScale.ObsessivePassion",
      },
    ],
    unavailableMeasures: [
      "Relatedness / Social Profile",
    ],
  },
];

const getNestedValue = (object, path) =>
  path.split(".").reduce(
    (value, key) =>
      value !== undefined && value !== null ? value[key] : undefined,
    object
  );

export const getNormalisedMeasureScore = (resultSet, measure) => {
  const raw = getNestedValue(resultSet, measure.path);
  if ((typeof raw !== "number" && typeof raw !== "string") || String(raw).trim() === "") return null;
  const value = Number(raw);
  const availability = resultSet.scoreAvailability && resultSet.scoreAvailability[measure.path];
  if (availability && !availability.available) return null;
  const feedback = getNestedValue(ResultFeedback, measure.feedbackPath);
  const max = Number(availability ? availability.scaleMax : feedback && feedback.scale && feedback.scale.max);

  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
    return null;
  }

  return Math.max(0, Math.min(100, (value / max) * 100));
};

export const getReportClusters = (resultSet) =>
  REPORT_CLUSTERS.map((cluster) => {
    const measures = cluster.measures
      .map((measure) => ({
        ...measure,
        score: getNormalisedMeasureScore(resultSet, measure),
      }));
    const available = measures.filter((measure) => Number.isFinite(measure.score));

    const clusterScore =
      available.length > 0
        ? available.reduce((sum, measure) => sum + measure.score, 0) /
          available.length
        : null;

    return {
      ...cluster,
      measures,
      clusterScore,
    };
  });

